import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { createContext, use } from 'react';

import { cn } from '@veraclins-dev/utils';

import { type WithTrigger } from '../types';

import { useSafeAreaCollisionPadding } from './utils/safe-area';
import { tooltipTriggerClassName } from './utils/variants/disabled-trigger';
import { TOOLTIP_CLASSES } from './utils/variants/shared-classes';

const InsideTooltipProviderContext = createContext(false);

/**
 * TooltipProvider component that wraps the tooltip functionality.
 * Provides context for tooltip positioning and behavior.
 *
 * Wrap a group of tooltips (a toolbar, or the whole app) in one provider so
 * they share timing: the first tooltip waits `delayDuration`, and moving to a
 * neighbour within `skipDelayDuration` opens it at once without animating.
 * A `Tooltip` outside any provider wraps itself in its own.
 */
function TooltipProvider({
  delayDuration = 200,
  skipDelayDuration = 300,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    >
      <InsideTooltipProviderContext value={true}>
        {children}
      </InsideTooltipProviderContext>
    </TooltipPrimitive.Provider>
  );
}

type TooltipProps = React.ComponentProps<typeof TooltipPrimitive.Root>;

/**
 * Root Tooltip component that manages the tooltip state and positioning.
 *
 * @example
 * ```tsx
 * <Tooltip>
 *   <TooltipTrigger>Hover me</TooltipTrigger>
 *   <TooltipContent>Tooltip content</TooltipContent>
 * </Tooltip>
 * ```
 */
function Tooltip({ ...props }: TooltipProps) {
  const root = <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
  if (use(InsideTooltipProviderContext)) {
    return root;
  }
  return <TooltipProvider>{root}</TooltipProvider>;
}

/**
 * TooltipTrigger component that triggers the tooltip on hover or focus.
 *
 * It renders its own `<button>`. When the child is already interactive (a
 * `Button`, a `Link`), pass `asChild` so the child becomes the trigger.
 * Without `asChild` the DOM nests a button inside a button, which is invalid
 * HTML: keyboard users get two tab stops and screen readers misname the
 * control. Leave `asChild` off only for plain content such as text or an
 * `Icon`, and give an icon-only trigger an `aria-label`.
 *
 * @example
 * ```tsx
 * <TooltipTrigger asChild>
 *   <Button>Hover me</Button>
 * </TooltipTrigger>
 * ```
 */
const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * TooltipArrow component that adds a visual arrow to the tooltip.
 *
 * @example
 * ```tsx
 * <TooltipContent>
 *   Tooltip content
 *   <TooltipArrow className="fill-current" />
 * </TooltipContent>
 * ```
 */
const TooltipArrow = ({
  className,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Arrow>) => (
  <TooltipPrimitive.Arrow
    data-slot="tooltip-arrow"
    className={cn(className)}
    {...props}
  />
);

type TooltipContentProps = React.ComponentProps<
  typeof TooltipPrimitive.Content
>;

/**
 * TooltipContent component that renders the tooltip content.
 *
 * Renders into a portal on `document.body`, like `SelectContent`,
 * `DropdownMenuContent` and `HoverCardContent`. The content is therefore not a
 * DOM descendant of the trigger, so a clipping ancestor no longer cuts it off:
 * an `overflow: hidden` panel that also carries a `transform` used to slice the
 * bubble in half. Tests must query it through `screen` or
 * `within(document.body)`, not `within(container)`.
 *
 * Radix shifts the content to keep it on screen. The default
 * `collisionPadding` is each edge's device safe-area inset, so a shifted
 * popup stops short of a landscape cutout or the home indicator instead of
 * sitting flush against the screen edge. The insets are 0 unless the app sets
 * `viewport-fit=cover`. A `collisionPadding` you pass replaces this default.
 *
 * @example
 * ```tsx
 * <TooltipContent>
 *   This is the tooltip content
 * </TooltipContent>
 * ```
 */
const TooltipContent = ({
  className,
  forceMount,
  sideOffset = 2,
  collisionPadding: collisionPaddingProp,
  ...props
}: TooltipContentProps) => {
  const collisionPadding = useSafeAreaCollisionPadding();
  return (
    // `forceMount` has to reach the portal: Radix gates it on `forceMount ||
    // open` there as well as on the content, so content asking to stay mounted
    // while closed renders nothing without it. The content reads the same value
    // back from the portal's context. It is passed there too so both halves
    // state the intent.
    <TooltipPrimitive.Portal forceMount={forceMount}>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        forceMount={forceMount}
        sideOffset={sideOffset}
        collisionPadding={collisionPaddingProp ?? collisionPadding}
        className={cn(TOOLTIP_CLASSES, className)}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
};

/**
 * Props for the ComposedTooltip component
 */
type ComposedTooltipProps<P extends object> = WithTrigger<P> & {
  /** The content to display in the tooltip */
  content: React.ReactNode;
  /** Whether to show an arrow on the tooltip */
  arrow?: boolean;
  /**
   * Milliseconds to wait before the tooltip opens. When omitted, the enclosing
   * `TooltipProvider` value applies (200 by default). A value set here
   * overrides the provider for this tooltip only.
   */
  delayDuration?: TooltipProps['delayDuration'];
} & Omit<TooltipContentProps, 'content'>;

/**
 * ComposedTooltip component that combines all tooltip parts into a single component.
 * Provides a simpler API for common tooltip use cases.
 *
 * @example
 * ```tsx
 * <ComposedTooltip
 *   Trigger={Button}
 *   content="This is a tooltip"
 *   TriggerProps={{ children: 'Hover me' }}
 * />
 * ```
 * @category overlays-modals
 */
const ComposedTooltip = <P extends { disabled?: boolean; className?: string }>({
  Trigger,
  content,
  arrow = true,
  TriggerProps,
  delayDuration,
  ...props
}: ComposedTooltipProps<P>) => {
  // A disabled trigger ignores the pointer, so the tooltip could not open on
  // hover. The trigger keeps this prop even when it chooses not to put the DOM
  // attribute on the element, as `Button` does while it stays focusable.
  const className = tooltipTriggerClassName(TriggerProps);
  return (
    <Tooltip delayDuration={delayDuration}>
      <TooltipTrigger asChild>
        <Trigger {...TriggerProps} className={className} />
      </TooltipTrigger>
      <TooltipContent {...props}>
        {content}
        {arrow && (
          <TooltipArrow height={8} width={16} className="fill-neutral-muted" />
        )}
      </TooltipContent>
    </Tooltip>
  );
};

export {
  ComposedTooltip,
  type ComposedTooltipProps,
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
};
